import React from "react";
import {
  Dialog,
  DialogContent,
  Stack,
  Box,
  Typography,
  Button,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  FiAlertTriangle,
  FiTrash2,
  FiAlertCircle,
  FiInfo,
  FiCheckCircle,
} from "react-icons/fi";

const VARIANTS = {
  danger: {
    icon: FiTrash2,
    iconColor: "#DC2626",
    iconBg: "#FEE2E2",
    confirmBg: "#DC2626",
    confirmHover: "#B91C1C",
  },
  warning: {
    icon: FiAlertTriangle,
    iconColor: "#D97706",
    iconBg: "#FEF3C7",
    confirmBg: "#D97706",
    confirmHover: "#B45309",
  },
  info: {
    icon: FiInfo,
    iconColor: "#0D9488",
    iconBg: "#F0FDFA",
    confirmBg: "#0D9488",
    confirmHover: "#0F766E",
  },
  success: {
    icon: FiCheckCircle,
    iconColor: "#16A34A",
    iconBg: "#DCFCE7",
    confirmBg: "#16A34A",
    confirmHover: "#15803D",
  },
  default: {
    icon: FiAlertCircle,
    iconColor: "#475569",
    iconBg: "#F1F5F9",
    confirmBg: "#0F172A",
    confirmHover: "#1E293B",
  },
};

const ConfirmDialog = ({
  open,
  onClose,
  onConfirm,
  title = "Are you sure?",
  description = "",
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  variant = "default",
  loading = false,
  hideCancel = false,
}) => {
  const cfg = VARIANTS[variant] || VARIANTS.default;
  const Icon = cfg.icon;

  const handleConfirm = async () => {
    try {
      await onConfirm?.();
    } catch (e) {
      // caller handles its own errors; we just don't crash here
    }
  };

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "12px",
          overflow: "hidden",
          boxShadow: "0 20px 60px rgba(15,23,42,0.18)",
        },
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        {/* Header */}
        <Stack
          direction="row"
          alignItems="flex-start"
          spacing={1.5}
          sx={{ p: 2.5, pb: 1.5 }}
        >
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: "10px",
              background: cfg.iconBg,
              color: cfg.iconColor,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <Icon size={18} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: "15px",
                fontWeight: 700,
                color: "#0F172A",
                lineHeight: 1.3,
              }}
            >
              {title}
            </Typography>
            {description && (
              <Typography
                sx={{
                  mt: 0.6,
                  fontSize: "13px",
                  color: "#475569",
                  lineHeight: 1.55,
                }}
              >
                {description}
              </Typography>
            )}
          </Box>
          {!loading && (
            <IconButton
              size="small"
              onClick={onClose}
              sx={{
                color: "#94A3B8",
                "&:hover": { color: "#0F172A", background: "#F1F5F9" },
                ml: -0.5,
                mt: -0.5,
              }}
            >
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          )}
        </Stack>

        {/* Footer */}
        <Stack
          direction="row"
          justifyContent="flex-end"
          spacing={1}
          sx={{
            px: 2.5,
            py: 1.5,
            background: "#FAFBFC",
            borderTop: "1px solid #F1F2F4",
          }}
        >
          {!hideCancel && (
            <Button
              onClick={onClose}
              disabled={loading}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                fontSize: "13px",
                color: "#475569",
                border: "1px solid #E5E7EB",
                background: "#FFFFFF",
                px: 2,
                py: 0.6,
                borderRadius: "8px",
                "&:hover": { background: "#F9FAFB", borderColor: "#D1D5DB" },
              }}
            >
              {cancelLabel}
            </Button>
          )}
          <Button
            onClick={handleConfirm}
            disabled={loading}
            variant="contained"
            sx={{
              textTransform: "none",
              fontWeight: 600,
              fontSize: "13px",
              background: cfg.confirmBg,
              color: "#FFFFFF",
              px: 2,
              py: 0.6,
              borderRadius: "8px",
              boxShadow: "none",
              "&:hover": { background: cfg.confirmHover, boxShadow: "none" },
              "&.Mui-disabled": { background: "#CBD5E1", color: "#FFFFFF" },
            }}
          >
            {loading ? "Working…" : confirmLabel}
          </Button>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default ConfirmDialog;
